import {Dispatch} from 'redux';
import {IResponse} from './Request';
import ErrorManager from './ErrorManager';
import NotificationToast from '../utils/NotificationToast';
import EHttpStatusCode from '../enums/EHttpStatusCodes';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

function isMutatingRequest(response: IResponse): boolean {
  const method = response.method?.toUpperCase()
  return !!method && MUTATING_METHODS.has(method)
}

function isSuccessStatus(statusCode: number): boolean {
  return statusCode >= 200 && statusCode < 300
}

function resolveResponseMessage(response: IResponse): string | undefined {
  if (typeof response.data === 'string') {
    return response.data
  }

  if (typeof response.data?.message === 'string') {
    return response.data.message
  }

  if (typeof (response as any).message === 'string') {
    return (response as any).message
  }

  return undefined
}

function fallbackSuccessMessage(method?: string): string {
  return 'Acción realizada exitosamente'
}

function fallbackErrorMessage(statusCode: number): string {
  if (statusCode === EHttpStatusCode.BAD_REQUEST) {
    return 'No fue posible procesar la solicitud'
  }

  if (statusCode === EHttpStatusCode.UNAUTHORIZED) {
    return 'Tu sesion ha expirado'
  }

  if (statusCode >= 500) {
    return 'Error interno del servidor. Intenta nuevamente.'
  }

  if (statusCode === 0) {
    return 'Error de red. Verifica tu conexion e intenta nuevamente.'
  }

  return 'No fue posible completar la operacion'
}

function shouldSkipToast(response: IResponse): boolean {
  if (response.silent) {
    return true
  }

  if (response.statusCode === EHttpStatusCode.UNAUTHORIZED) {
    return true
  }

  // Auth module is intentionally out of scope for this migration.
  if (response.path?.startsWith('/auth/')) {
    return true
  }

  return false
}

export default (dispatch: Dispatch, response: IResponse) => {
  ErrorManager(dispatch, response)

  if (!isMutatingRequest(response) || shouldSkipToast(response)) {
    return
  }

  const backendMessage = resolveResponseMessage(response)
  const message = isSuccessStatus(response.statusCode)
    ? backendMessage || fallbackSuccessMessage(response.method)
    : backendMessage || fallbackErrorMessage(response.statusCode)

  const toastId = NotificationToast.buildRequestToastId({
    method: response.method,
    path: response.path,
    statusCode: response.statusCode,
    message,
  })

  if (isSuccessStatus(response.statusCode)) {
    NotificationToast.success({
      message,
      toastId,
    })
    return
  }

  NotificationToast.error({
    message,
    toastId,
  })
}
